import React, { useEffect, useRef, useState } from "react";
import { Alert, Avatar, Box, Typography, keyframes } from "@mui/material";
import { kioskNoticeText } from "src/utils/employeeSessionEvents";
import useEmployeeTakeoverConfirm from "src/components/employeeTakeover/useEmployeeTakeoverConfirm";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "src/redux/store";
import {
  closeKiosk,
  getCurrentClockingSuccess,
  GetAllEmployees,
  setEmployee,
  addAdminEmployee,
  changeOpenClockingModal,
  GetPendingSignatures,
  setKioskIntent,
  setKioskReturnAfterClocking,
} from "src/redux/slices/employee";
import createAvatar from "src/utils/createAvatar";
import useAuth from "src/hooks/useAuth";
import axios from "src/utils/axios";
import {
  loginByPin,
  issueEmployeeSessionWithoutPin,
  logoutEmployee,
  verifyEmployeePin,
} from "src/redux/slices/employeeSession";
import { getDeviceId } from "src/utils/deviceId";
import { countForgottenShifts, planKioskClocking } from "src/utils/clocking/clockingFlow";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PrintIcon from "@mui/icons-material/Print";
import CoffeeIcon from "@mui/icons-material/Coffee";
import LocalPizzaIcon from "@mui/icons-material/LocalPizza";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import TapasIcon from "@mui/icons-material/Tapas";
import useResponsive from "src/hooks/useResponsive";
import { openClockingIfDue } from "src/utils/clocking/clockingDue";

// ── Animaciones ───────────────────────────────────────────────────────────────
const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(99,102,241,0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(99,102,241,0); }
  100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
`;

const pulseGreen = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
  70%  { box-shadow: 0 0 0 10px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
`;

const shakeRed = keyframes`
  0%   { transform: translateX(0);  background-color: #ef4444; }
  20%  { transform: translateX(-8px); }
  40%  { transform: translateX(8px); }
  60%  { transform: translateX(-6px); }
  80%  { transform: translateX(6px); }
  100% { transform: translateX(0);  background-color: #ef4444; }
`;

// ── Dots de PIN ───────────────────────────────────────────────────────────────
function PinDisplay({ value, error }) {
  const MAX = 8;
  const filled = value?.length ?? 0;
  // muestra hasta MAX dots pero al menos los introducidos
  const shown = Math.max(4, filled);
  return (
    <Box
      sx={{ display: "flex", justifyContent: "center", gap: 1.5, mb: 3, mt: 1 }}
    >
      {Array.from({ length: shown }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: error
              ? "error.main"
              : i < filled
                ? "primary.main"
                : "grey.300",
            transition: "background-color 0.12s",
            animation: error ? `${shakeRed} 0.45s ease` : "none",
          }}
        />
      ))}
    </Box>
  );
}

// ── Numpad ────────────────────────────────────────────────────────────────────
const NUM_ROWS = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  ["⌫", 0],
];

function Numpad({ onDigit, onBackspace }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 1.5,
        width: "100%",
        maxWidth: 400,
        mx: "auto",
      }}
    >
      {NUM_ROWS.flat().map((btn, i) => {
        const isDel = btn === "⌫";
        // la fila final tiene solo 2 botones: ⌫ ocupa col 1, 0 ocupa col 2-3
        const isZero = btn === 0;
        return (
          <Box
            key={i}
            component="button"
            type="button"
            onClick={() => (isDel ? onBackspace() : onDigit(String(btn)))}
            sx={{
              height: 68,
              gridColumn: isZero ? "2 / 4" : "auto",
              borderRadius: 3,
              border: "none",
              cursor: "pointer",
              fontSize: isDel ? 20 : 24,
              fontWeight: 700,
              fontFamily: "inherit",
              transition: "transform 0.08s, box-shadow 0.08s",
              bgcolor: isDel ? "error.main" : "rgba(255,255,255,0.75)",
              backdropFilter: "blur(8px)",
              color: isDel ? "#fff" : "text.primary",
              boxShadow: isDel
                ? "0 2px 8px rgba(239,68,68,0.3)"
                : "0 2px 12px rgba(99,102,241,0.10), 0 1px 3px rgba(0,0,0,0.07)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDel
                  ? "0 6px 16px rgba(239,68,68,0.35)"
                  : "0 6px 18px rgba(0,0,0,0.13)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            {btn}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function KioskOverlay() {
  const dispatch = useDispatch();
  const width = useResponsive("down", "sm");

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const kioskNotice = useSelector((s) => s.employees.kioskNotice);
  const { confirmTakeover, takeoverDialog } = useEmployeeTakeoverConfirm();
  const noticeText = kioskNoticeText(kioskNotice);
  const { employees, kioskManual, kioskIntent } = useSelector(
    (s) => s.employees,
  );
  const { user } = useAuth();

  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(false);
  const errorTimer = useRef(null);

  useEffect(() => {
    dispatch(GetAllEmployees());
  }, []);
  useEffect(() => () => clearTimeout(errorTimer.current), []);

  // 2026-10-07 · «Fichar» NO cambia el empleado de la tablet. Se comprueba el
  // PIN (sin emitir sesión ni tocar el dispositivo) y se abre el modal de
  // fichaje de ese empleado; la tablet queda como estaba (admin, kiosko u otro
  // empleado). Solo «Cambiar empleado» identifica a alguien en el dispositivo.
  const clockOnly = async (emp, pinPlaintext = null) => {
    const plan = planKioskClocking({ emp, pin: pinPlaintext, kioskManual });
    if (plan.type === "admin") {
      // El admin no ficha: se vuelve a la selección normal.
      dispatch(setKioskIntent(null));
      setSelected(null);
      setPin("");
      setTab(0);
      return;
    }
    if (plan.verifyPin) {
      try {
        await dispatch(verifyEmployeePin(emp.id, pinPlaintext, getDeviceId(), "clocking"));
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || "PIN incorrecto";
        enqueueSnackbar(msg, { variant: "error" });
        triggerError();
        return;
      }
    }
    const forgotten = countForgottenShifts(emp);
    if (forgotten > 0) {
      enqueueSnackbar(
        forgotten === 1
          ? "Tienes 1 fichaje sin finalizar — ciérralo antes de continuar"
          : `Tienes ${forgotten} fichajes sin finalizar — ciérralos antes de continuar`,
        { variant: "warning", autoHideDuration: 6000 },
      );
    }
    // Kiosko forzado (nadie en la tablet): al cerrar el fichaje, vuelve.
    dispatch(setKioskReturnAfterClocking(plan.returnToKiosk));
    dispatch(getCurrentClockingSuccess(emp.id));
    dispatch(closeKiosk());
    dispatch(changeOpenClockingModal(true));
  };

  const identify = async (emp, pinPlaintext = null) => {
    if (kioskIntent === "clocking") {
      await clockOnly(emp, pinPlaintext);
      return;
    }
    if (emp.isAdmin) {
      window.localStorage.setItem("UserEmployeeId", 0);
      axios.defaults.headers.common.UserEmployeeId = 0;
      // Admin dueño operando el TPV → limpiamos cualquier sesión de
      // empleado previa (fire-and-forget). El middleware backend deja
      // pasar al admin por AllowRestaurantOwner=true sin necesidad de
      // X-Employee-Session.
      dispatch(logoutEmployee()).catch(() => {});
      dispatch(
        addAdminEmployee({
          id: 0,
          name: emp.name,
          isAdmin: true,
          working: true,
          isInUse: true,
        }),
      );
    } else {
      // F2 · Si el empleado tiene PIN configurado, PRIMERO validamos
      // contra el backend con el `employeeId` del empleado seleccionado
      // — solo si el backend acepta el par (empleado, PIN) aplicamos el
      // cambio localmente. Sin esto, un PIN mal escrito o compartido
      // con otro empleado dejaba el TPV "cambiado" pero con JWT
      // rechazado (o del empleado equivocado). Fix bug 2026-07-31.
      //
      // Sin PIN configurado → el empleado entra libre (comportamiento
      // legacy). El middleware backend acepta el header UserEmployeeId
      // sin sesión firmada.
      if (pinPlaintext && emp.password) {
        try {
          const res = await dispatch(
            loginByPin(pinPlaintext, getDeviceId(), emp.id),
          );
          if (res?.code === "AMBIGUOUS_PIN") {
            // No debería pasar aquí — hemos pasado employeeId. Si el
            // backend devuelve ambigüedad, es un bug: abortamos y
            // avisamos sin cambiar el TPV.
            enqueueSnackbar(
              "El PIN no ha podido validarse. Vuelve a intentarlo.",
              { variant: "error" },
            );
            triggerError();
            return;
          }
        } catch (err) {
          const msg =
            err?.response?.data?.message
              || err?.message
              || "PIN incorrecto";
          enqueueSnackbar(msg, { variant: "error" });
          triggerError();
          return; // NO aplicar cambio de empleado si el backend rechaza
        }
      } else {
        // Empleado sin PIN o cambio programático (setEmployee sin numpad):
        // limpiamos cualquier sesión previa para no dejar el token de un
        // empleado anterior "colgado" en el localStorage.
        dispatch(logoutEmployee()).catch(() => {});
        // 2026-08-24 · Emitimos JWT firmado incluso sin PIN — el backend
        // solo lo permite si el empleado NO tiene PIN configurado (guard
        // duro server-side). Sin esto cada request desde el TPV mandaba
        // `UserEmployeeId=X` sin firma y disparaba warning en el log.
        // fire-and-forget: si falla (backend viejo o PIN sí requerido),
        // el flujo cae al header legacy como antes — cero regresión.
        try {
          await dispatch(issueEmployeeSessionWithoutPin(emp.id, getDeviceId()));
        } catch { /* fallback silencioso al legacy header */ }
      }

      // Solo tras validar backend → aplicar cambio local. El hook
      // `useEmployeeRole` lee `localStorage.UserEmployeeId` y si viene
      // vacío o 0 cae en `setAllData()` (permisos admin). Sin estas
      // líneas, cambiar de admin a empleado normal dejaba operando con
      // permisos de admin. El backend usa este header para trazar quién
      // realizó cada acción — debe reflejar la identidad efectiva.
      window.localStorage.setItem("UserEmployeeId", emp.id);
      axios.defaults.headers.common.UserEmployeeId = emp.id;

      dispatch(setEmployee(emp.id));
      dispatch(getCurrentClockingSuccess(emp.id));
      // Trae los adjustments PENDING del empleado. Si la lista vuelve con
      // elementos, el `DashboardLayout` (que renderiza condicionalmente
      // `PendingSignaturesModal`) abrirá el modal automáticamente para
      // que el empleado firme/rechace sus modificaciones aquí mismo. El
      // empleado puede posponer y los pendientes reaparecen en su próxima
      // identificación.
      dispatch(GetPendingSignatures(emp.id));
    }

    // Si el kiosk se abrió desde el botón "Fichar" (sin sesión de
    // empleado activa), encadenamos el modal de fichaje tras la
    // identificación — así el usuario llega en un solo paso a lo que
    // realmente quería hacer en lugar de tener que volver a pulsar
    // "Fichar" después de identificarse.
    //
    // Excepción: el admin NO ficha (no es un empleado con turno en el
    // sistema de control horario, su `id` es 0 y no tiene `currentWorkItem`).
    // Si seleccionamos al admin desde el flujo "Fichar" abríamos el modal
    // sobre un usuario fantasma — confuso y sin sentido. Para el admin nos
    // limitamos a cerrar el kiosk; si quería fichar se equivocó de usuario,
    // y si quería entrar al TPV ya está dentro.
    //
    // Fichaje que toca al identificarse (entrada de un turno ya empezado o
    // salida de uno terminado que sigue abierto): lo decide `openClockingIfDue`
    // más abajo con el ajuste `requireClockIn` (misma regla en todos los
    // caminos que identifican; ver src/utils/clocking/clockingDue.js).

    // Contamos los fichajes olvidados usando el campo
    // `pendingOpenShifts` que el backend calcula (WorkScheduleItems de
    // los últimos 4 días con al menos un shift abierto). Filtramos los
    // de HOY porque un shift abierto de hoy es el empleado trabajando
    // en este momento — no un olvido. Si hay uno o más olvidados
    // forzamos abrir el modal + snackbar de aviso.
    const openShiftsCount = countForgottenShifts(emp);
    const hasForgotten = openShiftsCount > 0;

    const openClocking =
      (kioskIntent === "clocking" || hasForgotten) &&
      !emp.isAdmin;
    dispatch(closeKiosk());
    if (!openClocking && !emp.isAdmin) {
      openClockingIfDue({ dispatch, user, employee: emp });
    }
    if (openClocking) {
      if (hasForgotten) {
        enqueueSnackbar(
          openShiftsCount === 1
            ? "Tienes 1 fichaje sin finalizar — ciérralo antes de continuar"
            : `Tienes ${openShiftsCount} fichajes sin finalizar — ciérralos antes de continuar`,
          { variant: "warning", autoHideDuration: 6000 },
        );
      }
      dispatch(changeOpenClockingModal(true));
    }
  };

  const triggerError = () => {
    setPinError(true);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => {
      setPinError(false);
      setPin("");
    }, 600);
  };

  // Comparación client-side directa contra el PIN persistido en plano.
  // El PIN TPV vive en `emp.password` para todos los empleados (admin
  // usa `user.accessCode`, que se copia a `password` en `adminEntry`).
  // `passwordAccess` es la contraseña del login web, NO se evalúa aquí.
  // Regla dura del producto: PIN=Password, plano, sin round-trip.
  // Cuando el largo del PIN escrito iguala al persistido y no matchea,
  // triggerError. `identify` lanza `loginByPin` server-side para emitir
  // el JWT firmado y auditar.
  const checkPin = async (newPin) => {
    if (!selected) return;
    if (selected.password && selected.password === newPin) {
      await identify(selected, newPin);
      return;
    }
    const maxLen = selected.password?.length ?? 0;
    if (maxLen > 0 && newPin.length === maxLen) triggerError();
  };

  const handleDigit = (d) => {
    if (pinError) return;
    setPin((prev) => {
      const next = prev.length < 8 ? prev + d : prev;
      checkPin(next);
      return next;
    });
  };

  const handleBackspace = () => {
    if (pinError) return;
    setPin((p) => p.slice(0, -1));
  };

  const handleSelectEmployee = async (emp) => {
    // 2026-10-06 · Si está en uso en otro dispositivo (y el restaurante limita
    // a uno), se avisa antes: Cancelar no lo toma.
    // Fichar no identifica a nadie en la tablet: no desplaza, no hay aviso.
    if (kioskIntent !== "clocking" && !(await confirmTakeover(emp))) return;
    setSelected(emp);
    setPin("");
    setPinError(false);
    // Sin PIN configurado = entrada libre. `emp.password` es el PIN TPV
    // en plano tanto para admin (viene de `user.accessCode`) como para
    // empleado normal. `passwordAccess` es login web y NO se evalúa aquí.
    if (!emp.password) {
      identify(emp);
    } else {
      setTab(1);
    }
  };

  const regularEmployees = employees.filter((e) => e.isActive && !e.isAdmin);
  const adminInList = employees.find((e) => e.isAdmin);
  // Siempre usamos user.accessCode como PIN del admin, independientemente
  // de si el objeto ya estaba en Redux (que no guarda el password).
  const adminEntry = {
    ...(adminInList ?? {}),
    id: 0,
    name: adminInList?.name ?? user?.displayName ?? "Admin",
    isAdmin: true,
    avatar: adminInList?.avatar ?? null,
    currentWorkItem: adminInList?.currentWorkItem ?? null,
    password: user?.accessCode || null,
    passwordAccess: null,
  };
  const activeEmployees = [adminEntry, ...regularEmployees];
  const restaurantName =
    user?.nombreEmpresa || user?.displayName || "Restaurante";
  const restaurantLogo = user?.photoUrl;

  const tabSx = (active) => ({
    flex: 1,
    py: 0.6,
    px: 1,
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
    fontFamily: "inherit",
    transition: "all 0.2s",
    bgcolor: active ? "primary.main" : "transparent",
    color: active ? "#fff" : "text.secondary",
    animation: active ? `${pulse} 1.8s ease-in-out infinite` : "none",
  });

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* ── Panel izquierdo: branding ────────────────────────────────────── */}
      <Box
        sx={{
          width: "36%",
          flexShrink: 0,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          background:
            "linear-gradient(160deg, #0f172a 0%, #1e293b 60%, #0f2027 100%)",
          px: 5,
          py: 6,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decoración de fondo */}
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10%" cy="15%" r="180" fill="rgba(99,102,241,0.07)" />
            <circle cx="90%" cy="80%" r="220" fill="rgba(34,197,94,0.05)" />
            <circle cx="80%" cy="20%" r="80" fill="rgba(99,102,241,0.05)" />
            <circle cx="20%" cy="85%" r="100" fill="rgba(56,189,248,0.04)" />
            {/* líneas de cuadrícula sutil */}
            {[...Array(8)].map((_, i) => (
              <line
                key={i}
                x1={`${i * 14}%`}
                y1="0"
                x2={`${i * 14}%`}
                y2="100%"
                stroke="rgba(255,255,255,0.02)"
                strokeWidth="1"
              />
            ))}
            {[...Array(10)].map((_, i) => (
              <line
                key={i}
                x1="0"
                y1={`${i * 11}%`}
                x2="100%"
                y2={`${i * 11}%`}
                stroke="rgba(255,255,255,0.02)"
                strokeWidth="1"
              />
            ))}
          </svg>
        </Box>

        {/* Ilustración TPV */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <svg
            width="160"
            height="120"
            viewBox="0 0 160 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* pantalla TPV */}
            <rect
              x="20"
              y="8"
              width="120"
              height="76"
              rx="8"
              fill="rgba(99,102,241,0.15)"
              stroke="rgba(99,102,241,0.4)"
              strokeWidth="1.5"
            />
            <rect
              x="28"
              y="16"
              width="104"
              height="60"
              rx="4"
              fill="rgba(15,23,42,0.6)"
            />
            {/* líneas de ticket en pantalla */}
            <rect
              x="36"
              y="26"
              width="48"
              height="4"
              rx="2"
              fill="rgba(99,102,241,0.7)"
            />
            <rect
              x="36"
              y="34"
              width="36"
              height="3"
              rx="1.5"
              fill="rgba(255,255,255,0.2)"
            />
            <rect
              x="36"
              y="41"
              width="56"
              height="3"
              rx="1.5"
              fill="rgba(255,255,255,0.15)"
            />
            <rect
              x="36"
              y="48"
              width="40"
              height="3"
              rx="1.5"
              fill="rgba(255,255,255,0.15)"
            />
            {/* total */}
            <rect
              x="80"
              y="60"
              width="44"
              height="8"
              rx="3"
              fill="rgba(34,197,94,0.3)"
              stroke="rgba(34,197,94,0.5)"
              strokeWidth="1"
            />
            <rect
              x="86"
              y="63"
              width="28"
              height="3"
              rx="1.5"
              fill="rgba(34,197,94,0.8)"
            />
            {/* pie / soporte */}
            <rect
              x="68"
              y="84"
              width="24"
              height="8"
              rx="2"
              fill="rgba(99,102,241,0.25)"
            />
            <rect
              x="52"
              y="92"
              width="56"
              height="6"
              rx="3"
              fill="rgba(99,102,241,0.2)"
              stroke="rgba(99,102,241,0.3)"
              strokeWidth="1"
            />
            {/* punto verde activo */}
            <circle cx="124" cy="20" r="4" fill="#22c55e" />
            <circle cx="124" cy="20" r="7" fill="rgba(34,197,94,0.2)" />
          </svg>
        </Box>

        {/* Logo y nombre del restaurante */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2.5,
          }}
        >
          {restaurantLogo ? (
            <Box
              component="img"
              src={restaurantLogo}
              alt={restaurantName}
              sx={{
                maxWidth: 160,
                maxHeight: 160,
                objectFit: "contain",
                borderRadius: 4,
                bgcolor: "rgba(255,255,255,0.95)",
                p: 1.5,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 100,
                height: 100,
                fontSize: 40,
                fontWeight: 700,
                bgcolor: "rgba(99,102,241,0.8)",
                boxShadow: "0 8px 32px rgba(99,102,241,0.4)",
              }}
            >
              {restaurantName.charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Typography
            variant="h4"
            sx={{
              color: "common.white",
              fontWeight: 700,
              letterSpacing: 0.5,
              textAlign: "center",
              textShadow: "0 2px 12px rgba(0,0,0,0.5)",
            }}
          >
            {restaurantName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.45)", textAlign: "center" }}
          >
            Identifícate para continuar
          </Typography>
        </Box>

        {/* Footer TakeMyOrder */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 1,
              bgcolor: "rgba(255,255,255,0.1)",
              mb: 1,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.25)",
              letterSpacing: 2.5,
              textTransform: "uppercase",
              fontSize: 9,
            }}
          >
            Gestionado con
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontWeight: 800,
              fontSize: 15,
              letterSpacing: 1,
              fontFamily: "'Segoe UI', sans-serif",
            }}
          >
            Take
            <Box component="span" sx={{ color: "rgba(99,102,241,0.9)" }}>
              My
            </Box>
            Order
          </Typography>
        </Box>
      </Box>

      {/* ── Panel derecho ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          background:
            "linear-gradient(145deg, #f0f4ff 0%, #fafafa 50%, #f5f0ff 100%)",
        }}
      >
        {/* Patrón de puntos de fondo */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            backgroundImage:
              "radial-gradient(circle, rgba(99,102,241,0.12) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        {/* 2026-10-06 · Por qué se volvió al kiosko (informativo, no error):
            p. ej. «Carlos ya está siendo utilizado en otro dispositivo…». */}
        {noticeText && (
          <Alert
            severity="info"
            data-testid="kiosk-notice"
            sx={{ position: "relative", zIndex: 1, mx: { xs: 2, md: 4 }, mt: 2 }}
          >
            {noticeText}
          </Alert>
        )}
        {takeoverDialog}
        {/* Manchas de color suaves en esquinas */}
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 280,
            height: 280,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -80,
            left: "30%",
            width: 320,
            height: 320,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(34,197,94,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        {/* Iconos flotantes decorativos */}
        {[
          {
            Icon: RestaurantIcon,
            top: "8%",
            left: "6%",
            size: 38,
            rot: -15,
            color: "rgba(99,102,241,0.13)",
          },
          {
            Icon: PrintIcon,
            top: "6%",
            right: "8%",
            size: 32,
            rot: 10,
            color: "rgba(99,102,241,0.11)",
          },
          {
            Icon: LocalPizzaIcon,
            top: "22%",
            right: "5%",
            size: 44,
            rot: -8,
            color: "rgba(251,146,60,0.13)",
          },
          {
            Icon: CoffeeIcon,
            top: "42%",
            left: "4%",
            size: 30,
            rot: 12,
            color: "rgba(120,80,40,0.10)",
          },
          {
            Icon: ReceiptIcon,
            top: "60%",
            right: "6%",
            size: 36,
            rot: -20,
            color: "rgba(99,102,241,0.12)",
          },
          {
            Icon: RamenDiningIcon,
            top: "75%",
            left: "7%",
            size: 42,
            rot: 8,
            color: "rgba(239,68,68,0.10)",
          },
          {
            Icon: TableRestaurantIcon,
            top: "85%",
            right: "9%",
            size: 34,
            rot: -6,
            color: "rgba(99,102,241,0.10)",
          },
          {
            Icon: TapasIcon,
            top: "30%",
            left: "3%",
            size: 28,
            rot: 18,
            color: "rgba(34,197,94,0.10)",
          },
        ].map(({ Icon, size, rot, color, ...pos }, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              ...pos,
              pointerEvents: "none",
              zIndex: 0,
              transform: `rotate(${rot}deg)`,
              color,
              "& svg": { width: size, height: size },
            }}
          >
            <Icon sx={{ fontSize: size, color }} />
          </Box>
        ))}

        {/* Header */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            pt: 3,
            pb: 2,
          }}
        >
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {tab === 1
              ? `Hola, ${selected?.name}`
              : kioskIntent === "clocking"
                ? "¿Quién va a fichar?"
                : ""}
          </Typography>
          {/* Fichar sin cambiar de empleado (también desde un kiosko forzado). */}
          <Box
            component="button"
            type="button"
            data-testid="kiosk-clocking-toggle"
            onClick={() => {
              dispatch(setKioskIntent(kioskIntent === "clocking" ? null : "clocking"));
              setSelected(null);
              setPin("");
              setTab(0);
            }}
            sx={{
              ml: "auto",
              mr: kioskManual ? 1.5 : 0,
              border: "1.5px solid",
              borderColor: kioskIntent === "clocking" ? "primary.main" : "divider",
              borderRadius: 2,
              px: 2,
              py: 0.75,
              bgcolor: "transparent",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              color: kioskIntent === "clocking" ? "primary.main" : "text.secondary",
              fontFamily: "inherit",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            {kioskIntent === "clocking" ? "Identificarme" : "Fichar"}
          </Box>
          {kioskManual && (
            <Box
              component="button"
              type="button"
              onClick={() => dispatch(closeKiosk())}
              sx={{
                border: "1.5px solid",
                borderColor: "divider",
                borderRadius: 2,
                px: 2,
                py: 0.75,
                bgcolor: "transparent",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 14,
                color: "text.secondary",
                fontFamily: "inherit",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              Cerrar
            </Box>
          )}
        </Box>

        {/* Tabs */}
        {!width && (
          <Box sx={{ position: "relative", zIndex: 1, px: 4, mb: 3 }}>
            <Box
              sx={{
                display: "inline-flex",
                gap: 0.5,
                bgcolor: "grey.200",
                borderRadius: "12px",
                p: 0.4,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setTab(0)}
                sx={tabSx(tab === 0)}
              >
                Empleado
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => selected && setTab(1)}
                sx={{
                  ...tabSx(tab === 1),
                  opacity: selected ? 1 : 0.4,
                  cursor: selected ? "pointer" : "not-allowed",
                }}
              >
                Código
              </Box>
            </Box>
          </Box>
        )}

        {/* Contenido */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            overflow: "auto",
            px: 4,
            pb: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* TAB 0: empleados sin cards */}
          {tab === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                color="text.primary"
                mb={4}
              >
                ¿Quién eres?
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  justifyContent: "center",
                }}
              >
                {activeEmployees.map((emp) => {
                  const av = createAvatar(emp.name);
                  const hasTurno = !!emp.currentWorkItem;
                  return (
                    <Box
                      key={emp.id}
                      onClick={() => handleSelectEmployee(emp)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        cursor: "pointer",
                        width: 110,
                        transition: "transform 0.18s",
                        "&:hover": { transform: "translateY(-6px)" },
                        "&:hover .emp-avatar": {
                          boxShadow: `0 8px 28px ${av.color}66`,
                        },
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <Avatar
                          className="emp-avatar"
                          src={emp.avatar}
                          alt={emp.name}
                          sx={{
                            width: 88,
                            height: 88,
                            bgcolor: av.color,
                            fontSize: 34,
                            fontWeight: 700,
                            transition: "box-shadow 0.18s",
                            boxShadow: hasTurno
                              ? `0 0 0 3px #22c55e, 0 4px 16px ${av.color}44`
                              : `0 4px 16px ${av.color}33`,
                          }}
                        >
                          {av.name}
                        </Avatar>
                        {hasTurno && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              right: 4,
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              bgcolor: "#22c55e",
                              border: "2px solid #f8f9fb",
                              animation: `${pulseGreen} 2s ease-in-out infinite`,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        textAlign="center"
                        color="text.primary"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {emp.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* TAB 1: PIN */}
          {tab === 1 && selected && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                pt: 2,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setTab(0);
                  setPin("");
                  setPinError(false);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  border: "none",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  color: "primary.main",
                  fontFamily: "inherit",
                  fontWeight: 600,
                  fontSize: 14,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                ← Cambiar usuario
              </Box>

              <Avatar
                src={selected.avatar}
                alt={selected.name}
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: createAvatar(selected.name).color,
                  fontSize: 28,
                  fontWeight: 700,
                  boxShadow: `0 4px 20px ${createAvatar(selected.name).color}55`,
                }}
              >
                {createAvatar(selected.name).name}
              </Avatar>

              <Typography variant="h6" fontWeight={700} mt={0.5}>
                {selected.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Introduce tu código de acceso
              </Typography>

              <PinDisplay value={pin} error={pinError} />
              <Numpad onDigit={handleDigit} onBackspace={handleBackspace} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
